import { Queue, QueuePayload, QueueThresholds } from 'lib-shared-legacy-types';

import type { QueuesAlertsFormValues } from './queuesAlertsForm';
import type { FormValues as QueuesSettingsFormValues } from './queuesSettingsForm/types/settings';

const EMPTY_TIMEOUT_5_MINS = 300;
const EMPTY_TIMEOUT_12_HOURS = 43200;
export const EMPTY_CHANNEL_TIMEOUT = {
  facebook_messenger: EMPTY_TIMEOUT_5_MINS,
  email: EMPTY_TIMEOUT_12_HOURS,
  sms: EMPTY_TIMEOUT_5_MINS,
  voicemail: EMPTY_TIMEOUT_5_MINS,
  widget_chat: EMPTY_TIMEOUT_5_MINS,
  pstn_phone: EMPTY_TIMEOUT_5_MINS,
  callback: EMPTY_TIMEOUT_5_MINS,
  whats_app: EMPTY_TIMEOUT_5_MINS,
  contact_form: EMPTY_TIMEOUT_5_MINS,
};

export const createPayload = (
  values: QueuesSettingsFormValues,
  queue?: Queue,
): QueuePayload => {
  const doNotOfferTimeouts = {
    // phone
    pstn_phone: values.do_not_offer_timeouts.pstn_phone,
    voicemail: values.do_not_offer_timeouts.pstn_phone,
    callback: values.do_not_offer_timeouts.pstn_phone,
    // mail
    email: values.do_not_offer_timeouts.email,
    contact_form: values.do_not_offer_timeouts.email,
    // chat
    widget_chat: values.do_not_offer_timeouts.widget_chat,
    // messaging
    facebook_messenger: values.do_not_offer_timeouts.facebook_messenger,
    whats_app: values.do_not_offer_timeouts.facebook_messenger,
    sms: values.do_not_offer_timeouts.facebook_messenger,
  };

  const preferredAgentsTimouts = {
    // phone
    pstn_phone: values.preferred_agent_timeouts.pstn_phone,
    voicemail: values.preferred_agent_timeouts.pstn_phone,
    callback: values.preferred_agent_timeouts.pstn_phone,
    // mail
    email: values.preferred_agent_timeouts.email,
    contact_form: values.preferred_agent_timeouts.email,
    // chat
    widget_chat: values.preferred_agent_timeouts.widget_chat,
    // messaging
    facebook_messenger: values.preferred_agent_timeouts.facebook_messenger,
    whats_app: values.preferred_agent_timeouts.facebook_messenger,
    sms: values.preferred_agent_timeouts.facebook_messenger,
  };

  const payload: QueuePayload = {
    name: values.name,
    offer_algorithm: values.offer_algorithm,
    is_default: !!values.is_default,
    is_archived: !!values.is_archived,
    offer_abandoned_conversations: values.offer_abandoned_conversations,
    is_do_not_offer_enabled: !!values.is_do_not_offer_enabled,
    is_preferred_agent_enabled: values.is_preferred_agent_enabled,
    is_personal_offer_prioritized: values.is_personal_offer_prioritized,
    priority: values.priority,
    offer_timeout: values.offer_timeout,
    wrapup_timeout: values.wrapup_timeout,
    queue_thresholds: queue ? queue.queue_thresholds : null,
    do_not_offer_timeouts: doNotOfferTimeouts,
    preferred_agent_timeouts: preferredAgentsTimouts,
    preferred_agent_offline_timeout:
      values.preferred_agent_offline_timeout_enabled
        ? values.preferred_agent_offline_timeout
        : null,
    personal_agent_offline_timeout:
      values.personal_agent_offline_timeout_enabled
        ? values.personal_agent_offline_timeout
        : null,
  };

  return { ...payload };
};

export const createAlertsPayload = (
  values: QueuesAlertsFormValues,
  queue: Queue,
): QueuePayload => {
  const queueThresholds: QueueThresholds = {
    sla_percentage: values.sla_percentage,
    sla_time_limit: values.sla_time_limit,
    longest_wait: values.longest_wait,
    available_agents: values.available_agents,
    waiting_conversations: values.waiting_conversations,
  };

  if (values.is_sla_percentage) {
    queueThresholds.sla_percentage = values.sla_percentage;
    queueThresholds.sla_time_limit = values.sla_time_limit;
  } else {
    delete queueThresholds.sla_percentage;
    delete queueThresholds.sla_time_limit;
  }

  if (values.is_longest_wait) {
    queueThresholds.longest_wait = values.longest_wait;
  } else {
    delete queueThresholds.longest_wait;
  }

  if (values.is_available_agents) {
    queueThresholds.available_agents = values.available_agents;
  } else {
    delete queueThresholds.available_agents;
  }

  if (values.is_waiting_conversations) {
    queueThresholds.waiting_conversations = values.waiting_conversations;
  } else {
    delete queueThresholds.waiting_conversations;
  }

  return { ...queue, queue_thresholds: { ...queueThresholds } };
};

export const parsePriorityValue = (value: string): number | undefined => {
  try {
    const int = parseInt(value);
    if (!isNaN(int)) {
      return Math.max(1, int);
    }
    return int;
  } catch (error) {
    return undefined;
  }
};
